import { useState } from "react";
import { Application, UserRole } from "../model";
import { useAuth } from "@sky/xerxes-client-react";
import ApplicationService from "../service/ApplicationService";

type AccessStore = {
    applications: Application[];
    application: Application | undefined;
    roles: any[];
    loading: boolean;
    loadingRoles: boolean;
    fetchAll: () => void;
    fetchUserAppRoles: (userId: string, clientId: string) => void;
    addUserRoleToApp: (userId: string, clientId: string, role: UserRole) => void; 
    removeUserRoleFromApp: (userId: string, clientId: string, role: UserRole) => void; 
    clear: () => void;
}

const useAccessStore = (): AccessStore => {
    const { token } = useAuth();
    const [loading, setLoading] = useState<boolean>(false);

    const [loadingRoles, setLoadingRoles] = useState<boolean>(false);

    const [applications, setApplications] = useState<Application[]>([]);
    const [application, setApplication] = useState<Application | undefined>(undefined);

    const [roles, setRoles] = useState<any[]>([])


    const applicationService = new ApplicationService(token!);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const applications = await applicationService.fetchAll();
            const application = applications.at(0);
            setApplications(applications);
            setApplication(application)
        } catch (e) {
            setApplications([]);
            setApplication(undefined);
            console.error('Error fetching all applications', e)
        } finally {
            setLoading(false);
        }
    }

    const fetchUserAppRoles = async (userId: string, clientId: string) => {
        try {
            setLoadingRoles(true);
            setRoles([]);
            setApplication(applications.find(app => app.clientId === clientId))
            setRoles(await applicationService.fetchRolesForUser(userId, clientId));
        } catch (e) {
            setApplication(undefined)
            setRoles([]);
            console.error('Error fetching app roles', e)
        } finally {
            setLoadingRoles(false);
        }
    }

    const addUserRoleToApp = async (userId: string, clientId: string, role: UserRole) => {
        try {
            setLoadingRoles(true);
            await applicationService.addRoleForUser(userId, clientId, role);
            setRoles(await applicationService.fetchRolesForUser(userId, clientId));
        } catch (e) {
            console.error('Error fetching app roles', e)
        } finally {
            setLoadingRoles(false);
        }
    }

    const removeUserRoleFromApp = async (userId: string, clientId: string, role: UserRole) => {
        try {
            setLoadingRoles(true);
            await applicationService.removeRoleForUser(userId, clientId, role);
            setRoles(await applicationService.fetchRolesForUser(userId, clientId));
        } catch (e) {
            console.error('Error removing user role from app', e)
        } finally {
            setLoadingRoles(false);
        }
    }

    const clear = () => {
        setLoading(false);
        setLoadingRoles(false);
        setApplications([]);
        setApplication(undefined);
        setRoles([]);
    }

    return {
        applications, application, roles, loading, loadingRoles, fetchAll, fetchUserAppRoles, addUserRoleToApp, removeUserRoleFromApp, clear
    }
}

export default useAccessStore;